// External deps
import { useContext, useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import { Form } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';

// Internal deps
import { getNotification } from '../../../../services/adminContracts';
import { initialValuesSDS } from '../../../CreateServiceRequest/Constants';
import { useFilesContext } from '../../../../context/FilesProvider';
import LoaderContext from '../../../../context/LoaderProvider';
import MainTitle from '../../../MainTitle/MainTitle';
import useToast from '../../../../hooks/useToast';
import { getMultipleDraft as getDataFromTemplateToEdit } from '../../../../adapters/createServiceRequest/createServiceRequestAdapter';
import ListOfAccordions from '../../../CreateServiceRequest/ServiceForm/ListOfAccordions/ListOfAccordions';
import UbicationForm from '../../../CreateServiceRequest/UbicationForm/UbicationForm';

import validationSchema from './GenerateRequestTemplateUtils.js';
import GenerateRequestTemplateButtons from './GenerateRequestTemplateButtons/GenerateRequestTemplateButtons';

import './GenerateRequestTemplate.scss';

const GenerateRequestTemplate = () => {
  const [showTwoStep, setShowTwoStep] = useState(false);
  const [step, setStep] = useState(1);

  const [notificationOptions, setNotificationOptions] = useState([]);
  const setDeletedServices = useState([])[1];
  const [isAutocomplete, setIsAutocomplete] = useState(false);
  const form = useRef(null);
  const buttonsRef = useRef(null);
  // isDraw means info was loaded from an SDS draft
  // and when isDraw is true, we should receive its id
  // isOwn refere to the request template
  const { previusPath, isDraw, idDraw, onlyVisualizeRequestTemplate, isOwn } = useLocation();

  const { setLoader } = useContext(LoaderContext);
  const { setDataAdjuntos } = useFilesContext();
  const { setNewToast } = useToast();
  // id refers to a template id, received when editing a template
  const { id } = useParams();

  const addDeletedServiceRequest = (serviceRequest) => {
    if (serviceRequest?.serviceRequestId) {
      setDeletedServices((prevState) => [
        ...prevState,
        {
          id: serviceRequest?.serviceRequestId,
          eliminado: true
        }
      ]);
    }
  };

  const fetchNotification = async (idBusinessUnit, contractNumber) => {
    const notification = await getNotification(idBusinessUnit, contractNumber);

    setNotificationOptions(notification.data.opciones);
  };

  const getDataOfTemplate = async (templateId) => {
    try {
      setLoader(true);
      const formatedTemplate = await getDataFromTemplateToEdit(templateId, false, !!id, isDraw);

      if (formatedTemplate?.accordions[0]?.provider?.providers?.length) {
        const providerData = formatedTemplate.accordions[0].provider.providers[0];

        fetchNotification(providerData.businessUnit.id, providerData.contract.nombre);
      }
      form.current.setValues(formatedTemplate);
      const allAttachments = formatedTemplate?.accordions.map((acc) => acc.attachments).flat();

      setDataAdjuntos(allAttachments);
      setLoader(false);
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
      setLoader(false);
    }
  };

  useEffect(() => {
    // If there is an id, we are in edit view and must fetch the data
    // The isAutoComplete value identifies whether a template is for edit or draft
    // We pass it to ubicationForm; useUbicationForm uses it to avoid re-fetching
    // Also, depending on the user role, business unit data can be overlapped
    // When the role is provider notifier, they can see all business units
    // Other roles can only see business units associated to their user
    if (id) {
      getDataOfTemplate(id);
      setIsAutocomplete(true);
    }
    if (isDraw && idDraw) {
      getDataOfTemplate(idDraw);
      setIsAutocomplete(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isDraw, idDraw]);

  useEffect(() => {
    setDataAdjuntos([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValuesSDS}
      innerRef={form}
      validationSchema={validationSchema}
    >
      {/* eslint-disable-next-line no-unused-vars */}
      {({ values }) => (
        <Form>
          <div className="container">
            <div className="my-4">
              <MainTitle
                isSDS={false}
                nombre={
                  onlyVisualizeRequestTemplate
                    ? 'Visualización de plantilla'
                    : 'Crear plantilla de solicitud de servicio'
                }
                previusPath={previusPath}
              />
            </div>
            <UbicationForm
              autocompletWells
              disabledAllFields={onlyVisualizeRequestTemplate}
              fatherStep={step}
              id={id}
              includeGenericTypes={false}
              isAutocomplete={isAutocomplete}
              setFatherStep={setStep}
              setShowNextStep={setShowTwoStep}
            />
            {showTwoStep && (
              <ListOfAccordions
                addDeletedServiceRequest={addDeletedServiceRequest}
                disabledAllFields={onlyVisualizeRequestTemplate}
                isAutocomplete={isAutocomplete}
                notificationOptions={notificationOptions}
              />
            )}
            <div className="gap row justify-content-center" ref={buttonsRef}>
              <GenerateRequestTemplateButtons
                id={id}
                isDraft={!!id}
                isDraw={isDraw}
                previusPath={previusPath}
                requestTemplateIsOwn={isOwn}
                visualizePublicTemplate={onlyVisualizeRequestTemplate}
              />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default GenerateRequestTemplate;
